import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const LOCATION_LIST_DEFAULT_STATE = {
  searchQuery: '',
  countryFilter: 'All',
  stateFilter: 'All',
  metaFilter: 'All',
  contentFilter: 'All',
  currentPage: 1,
  sortColumn: 8,
  sortDir: 'desc'
};

let locationListStateCache = null;

const SearchableSelect = ({ value, onChange, options, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => opt._id === value);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent text-left flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="text-sm">{selectedOption ? selectedOption.name : placeholder}</span>
        <i className={`bi bi-chevron-down text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredOptions.map(option => (
              <button
                key={option._id}
                type="button"
                onClick={() => {
                  onChange(option._id);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className="w-full px-2 sm:px-4 py-2 text-sm text-left hover:bg-[#009ef7]/10 transition-colors"
              >
                {option.name}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-2 sm:px-4 py-2 text-sm text-gray-500">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Location = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const cachedState = locationListStateCache || LOCATION_LIST_DEFAULT_STATE;
  const [locations, setLocations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(cachedState.searchQuery);
  const [countryFilter, setCountryFilter] = useState(cachedState.countryFilter);
  const [stateFilter, setStateFilter] = useState(cachedState.stateFilter);
  const [metaFilter, setMetaFilter] = useState(cachedState.metaFilter);
  const [contentFilter, setContentFilter] = useState(cachedState.contentFilter);
  const [currentPage, setCurrentPage] = useState(cachedState.currentPage);
  const [sortColumn, setSortColumn] = useState(cachedState.sortColumn ?? 8);
  const [sortDir, setSortDir] = useState(cachedState.sortDir ?? 'desc');
  const [totalPages, setTotalPages] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const mediaUrl = process.env.REACT_APP_MEDIA_URL || '';
  const [associationModal, setAssociationModal] = useState(null); // { id, name, response }
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [currentPage, searchQuery, countryFilter, stateFilter, metaFilter, contentFilter, sortColumn, sortDir, pageLimit]);

  useEffect(() => {
    if (countryFilter && countryFilter !== 'All') {
      fetchStates(countryFilter);
    } else {
      setStates([]);
      setStateFilter('All');
    }
  }, [countryFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    locationListStateCache = {
      searchQuery,
      countryFilter,
      stateFilter,
      metaFilter,
      contentFilter,
      currentPage,
      sortColumn,
      sortDir
    };
  }, [searchQuery, countryFilter, stateFilter, metaFilter, contentFilter, currentPage, sortColumn, sortDir]);

  const fetchCountries = async () => {
    try {
      const response = await apiService.get('countryList');
      setCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const response = await apiService.get(`stateList/${countryId}`);
      setStates(response.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const response = await apiService.post('locationList', {
        country: countryFilter,
        state: stateFilter,
        metapending: metaFilter,
        pending: contentFilter,
        search: searchQuery,
        start: (currentPage - 1) * pageLimit,
        length: pageLimit,
        order: [{ column: sortColumn, dir: sortDir }]
      });
      
      if (response.data) {
        setLocations(response.data);
        setTotalPages(Math.ceil(response.recordsFiltered / pageLimit));
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLocationStatus = async (id) => {
    try {
      await apiService.get(`locationStatus/${id}`);
      setLocations(prevLocations => 
        prevLocations.map(location => 
          location._id === id ? { ...location, status: location.status === 1 ? 0 : 1 } : location
        )
      );
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const toggleTopStatus = async (id) => {
    try {
      await apiService.get(`location_top_Status/${id}`);
      setLocations(prevLocations => 
        prevLocations.map(location => 
          location._id === id ? { ...location, top: location.top === 1 ? 0 : 1 } : location
        )
      );
      toast.success('Top status updated successfully');
    } catch (error) {
      toast.error('Failed to update top status');
      console.error(error);
    }
  };

  const checkAssociations = async (id, name) => {
    try {
      const response = await apiService.get(`checkLocationAssociations/${id}`);
      setAssociationModal({ id, name, response });
    } catch (error) {
      toast.error('Error checking associations');
    }
  };

  const handleDeleteLocation = async () => {
    setDeleteLoading(true);
    try {
      const response = await apiService.delete(`deleteLocation/${associationModal.id}`);
      if (response.success || response.status) {
        toast.success('Location deleted successfully');
        setAssociationModal(null);
        fetchLocations();
      } else {
        toast.error(response.message || 'Failed to delete location');
      }
    } catch (error) {
      toast.error('Error deleting location');
    } finally {
      setDeleteLoading(false);
    }
  };

  const makePublic = async (id) => {
    try {
      await apiService.post('makePublic', { id, type: 'location' });
      toast.success('Published successfully');
      setLocations(prevLocations =>
        prevLocations.map(location =>
          location._id === id ? { ...location, vendorId: null } : location
        )
      );
    } catch (error) {
      toast.error('Failed to make public');
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSort = (columnIndex) => {
    if (sortColumn === columnIndex) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(columnIndex);
    setSortDir('asc');
  };

  const renderSortIcon = (columnIndex) => {
    if (sortColumn !== columnIndex) {
      return <i className="bi bi-arrow-down-up text-[10px] text-gray-400"></i>;
    }
    return <i className={'bi ' + (sortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down') + ' text-[10px] text-[#009ef7]'}></i>;
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key="first" onClick={() => setCurrentPage(1)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">«</button>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 border rounded-lg ${currentPage === i ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-50'}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      pages.push(
        <button key="last" onClick={() => setCurrentPage(totalPages)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">»</button>
      );
    }

    return pages;
  };

  return (
    <>
    <div className="p-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <nav className="text-xs sm:text-sm flex flex-wrap items-center">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Locations</span>
        </nav>
        {hasPermission('location_page', 'location', 'add') && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Link to="/view-months" className="px-2 sm:px-4 py-2 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 transition-colors no-underline">
              <i className="bi bi-calendar mr-2"></i>Manage Months
            </Link>
            <Link to="/add-location" className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white text-sm rounded-lg hover:bg-[#009ef7] transition-colors no-underline">
              <i className="bi bi-plus-lg mr-2"></i>Add New
            </Link>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex flex-col sm:flex-row flex-wrap gap-3">
          <div className="flex-1 min-w-full sm:min-w-[200px]">
            <div className="relative">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search query here.."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
          </div>
          <SearchableSelect
            value={countryFilter}
            onChange={(value) => {
              setCountryFilter(value);
              setStateFilter('All');
            }}
            options={[{ _id: 'All', name: 'All Country' }, ...countries]}
            placeholder="All Country"
          />
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            disabled={!countryFilter || countryFilter === 'All'}
            className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent disabled:opacity-50"
          >
            <option value="All">All States</option>
            {states.map(state => (
              <option key={state._id} value={state._id}>{state.name}</option>
            ))}
          </select>
          {hasPermission('location_page', 'location', 'edit') && (
            <>
              <select
                value={metaFilter}
                onChange={(e) => setMetaFilter(e.target.value)}
                className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              >
                <option value="All">Show All</option>
                <option value="1">Meta Data</option>
                <option value="0">No Meta Data</option>
              </select>
              <select
                value={contentFilter}
                onChange={(e) => setContentFilter(e.target.value)}
                className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              >
                <option value="All">Show All</option>
                <option value="1">Content</option>
                <option value="0">No Image/About</option>
              </select>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto" style={{overflowX: 'scroll'}}>
          <table className="text-xs sm:text-sm" style={{minWidth: '1400px'}}>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase" style={{minWidth: '100px'}}>Image</th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  <button type="button" onClick={() => handleSort(2)} className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors">
                    Name
                    {renderSortIcon(2)}
                  </button>
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  <button type="button" onClick={() => handleSort(3)} className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors">
                    Country
                    {renderSortIcon(3)}
                  </button>
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  <button type="button" onClick={() => handleSort(4)} className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors">
                    State
                    {renderSortIcon(4)}
                  </button>
                </th>
                {hasPermission('location_page', 'location', 'edit') && (
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Live</th>
                )}
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">View</th>
                {hasPermission('location_page', 'location', 'edit') && (
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Top</th>
                )}
                {user?.role === 'admin' && (
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    <button type="button" onClick={() => handleSort(8)} className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors">
                      Public
                      {renderSortIcon(8)}
                    </button>
                  </th>
                )}
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  <button type="button" onClick={() => handleSort(9)} className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors">
                    Created At
                    {renderSortIcon(9)}
                  </button>
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  <button type="button" onClick={() => handleSort(10)} className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors">
                    Updated At
                    {renderSortIcon(10)}
                  </button>
                </th>
                {hasPermission('location_page', 'location', 'edit') && (
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="12" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#009ef7]"></div>
                      <p className="mt-3 text-sm text-gray-500">Loading...</p>
                    </div>
                  </td>
                </tr>
              ) : locations.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-4 py-8 text-center text-gray-500">No locations found</td>
                </tr>
              ) : (
                locations.map((location, index) => (
                  <tr key={location._id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-900">{(currentPage - 1) * pageLimit + index + 1}</td>
                    <td className="px-4 py-3">
                      {location.image ? (
                        <img
                          src={`${mediaUrl}${location.image}`}
                          alt={location.name}
                          className="w-20 h-14 object-cover rounded"
                        />
                      ) : (
                        <div className="w-20 h-14 flex items-center justify-center bg-gray-100 rounded">
                          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-900 font-medium">
                      <div className="flex flex-col">
                        <span>{location.name}</span>
                        <span className="text-xs text-gray-400 font-normal">#{location.id}</span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-600">{location.countryId?.name || '--'}</td>
                    <td className="px-2 sm:px-4 py-3 text-sm text-gray-600">{location.stateId?.name || '--'}</td>
                    {hasPermission('location_page', 'location', 'edit') && (
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={location.status === 1}
                            onChange={() => toggleLocationStatus(location._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <a
                        href={`https://travellinglark.com/location/${location.countryId?.slug}/${location.stateId?.slug}/${location.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 bg-[#009ef7]/10 text-[#009ef7] rounded hover:bg-[#009ef7]/20 transition-colors"
                        title="View"
                      >
                        <i className="bi bi-eye"></i>
                      </a>
                    </td>
                    {hasPermission('location_page', 'location', 'edit') && (
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={location.top === 1}
                            onChange={() => toggleTopStatus(location._id)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                      </td>
                    )}
                    {user?.role === 'admin' && (
                      <td className="px-4 py-3">
                        {location.vendorId && (
                          <button
                            onClick={() => makePublic(location._id)}
                            className="inline-flex items-center justify-center w-8 h-8 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                            title="Make Public"
                          >
                            <i className="bi bi-check-lg text-green-600"></i>
                          </button>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-xs text-gray-600">{formatDate(location.createdAt)}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{formatDate(location.updatedAt)}</td>
                    {hasPermission('location_page', 'location', 'edit') && (
                      <td className="px-4 py-3">
                        <div className="relative" ref={openMenuId === location._id ? menuRef : null}>
                          <button
                            onClick={() => setOpenMenuId(openMenuId === location._id ? null : location._id)}
                            className="inline-flex items-center justify-center w-8 h-8 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <i className="bi bi-three-dots-vertical text-gray-600"></i>
                          </button>
                          {openMenuId === location._id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                              <Link
                                to={`/edit-location/${location._id}`}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg no-underline"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <i className="bi bi-pencil mr-2 text-green-600"></i>
                                Edit
                              </Link>
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  navigate(`/things-to-do/${location._id}`);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <i className="bi bi-list-task mr-2 text-blue-600"></i>
                                Things To Do
                              </button>
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  navigate(`/itinerary/${location._id}`);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <i className="bi bi-map mr-2 text-purple-600"></i>
                                Itinerary
                              </button>
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  navigate(`/places-to-visit/${location._id}`);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <i className="bi bi-geo-alt mr-2 text-orange-600"></i>
                                Places To Visit
                              </button>
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  checkAssociations(location._id, location.name);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
                              >
                                <i className="bi bi-trash mr-2"></i>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && locations.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs sm:text-sm text-gray-700">Show</span>
              <select
                value={pageLimit}
                onChange={(e) => { setPageLimit(Number(e.target.value)); setCurrentPage(1); }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white text-gray-700"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-700">entries</span>
              <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">({(currentPage - 1) * pageLimit + 1} to {Math.min(currentPage * pageLimit, (currentPage - 1) * pageLimit + locations.length)} of {totalPages * pageLimit})</span>
            </div>
            <div className="flex gap-1">
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>

      {associationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h5 className="text-base font-semibold">Delete Location: {associationModal.name}</h5>
              <button onClick={() => setAssociationModal(null)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {associationModal.response.status ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                  <i className="bi bi-check-circle-fill"></i>
                  This location can be safely deleted.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 text-yellow-700 rounded-lg mb-3">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    This location has associations and cannot be deleted:
                  </div>
                  {associationModal.response.data?.associations?.map((assoc, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg mb-3">
                      <div className="px-3 py-2 bg-gray-50 border-b font-medium text-sm">
                        {assoc.type.replace(/_/g, ' ').toUpperCase()} ({assoc.count})
                      </div>
                      <div className="p-3">
                        {assoc.data.slice(0, 5).map((item, j) => (
                          <div key={j} className="text-xs text-gray-600 py-1">
                            {item.name || item.description || `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.message || `ID: ${item._id}`}
                          </div>
                        ))}
                        {assoc.count > 5 && <div className="text-xs text-gray-400">... and {assoc.count - 5} more</div>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button onClick={() => setAssociationModal(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              {associationModal.response.status && (
                <button
                  onClick={handleDeleteLocation}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Location'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Location;

